/**
 * Error Interceptor
 *
 * Functional HTTP interceptor that handles error responses from the API.
 * Provides centralized error handling with user-friendly notifications using PrimeNG Toast.
 *
 * This interceptor follows the Angular 15+ functional interceptor pattern.
 *
 * Error handling strategy:
 * - 401 Unauthorized: Clear token, redirect to login, show notification
 * - 403 Forbidden: Show permission denied notification
 * - 500+ Server Errors: Show server error notification
 * - Network Errors: Show connection error notification
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * provideHttpClient(
 *   withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor])
 * )
 * ```
 */

import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuthService } from '../services/auth.service';
import { TranslationService } from '../services/translation.service';

/**
 * Intercepts HTTP errors and provides appropriate user feedback
 *
 * @param req - The outgoing HTTP request
 * @param next - The next handler in the interceptor chain
 * @returns Observable of the HTTP response with error handling
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const messageService = inject(MessageService);
  const translationService = inject(TranslationService);
  const t = (key: string) => translationService.translate(key);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle different error status codes
      switch (error.status) {
        case 401:
          // Unauthorized - Token invalid or expired
          handleUnauthorized(router, authService, messageService, t);
          break;

        case 403:
          // Forbidden - User doesn't have permission
          handleForbidden(messageService, t);
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          handleServerError(messageService, error, t);
          break;

        case 0:
          // Network error
          handleNetworkError(messageService, t);
          break;

        default:
          // Other errors - show generic message
          handleGenericError(messageService, error, t);
          break;
      }

      // Re-throw the error for further handling if needed
      return throwError(() => error);
    })
  );
};

/**
 * Handles 401 Unauthorized errors
 *
 * Clears authentication state and redirects to login page.
 *
 * @param router - Angular router for navigation
 * @param authService - Auth service to clear authentication state
 * @param messageService - PrimeNG message service for notifications
 */
function handleUnauthorized(
  router: Router,
  authService: AuthService,
  messageService: MessageService,
  t: (key: string) => string
): void {
  // Clear authentication state through AuthService
  authService.forceLogout();

  // Show error message
  messageService.add({
    severity: 'error',
    summary: t('toast.authRequired'),
    detail: t('toast.sessionExpired'),
    life: 5000
  });

  // Redirect to login page
  router.navigate(['/auth/login']);
}

/**
 * Handles 403 Forbidden errors
 *
 * Shows a permission denied message to the user.
 *
 * @param messageService - PrimeNG message service for notifications
 */
function handleForbidden(messageService: MessageService, t: (key: string) => string): void {
  messageService.add({
    severity: 'warn',
    summary: t('toast.permissionDenied'),
    detail: t('toast.noPermission'),
    life: 5000
  });
}

/**
 * Handles server errors (5xx status codes)
 *
 * Shows a server error message to the user.
 *
 * @param messageService - PrimeNG message service for notifications
 * @param error - The HTTP error response
 */
function handleServerError(messageService: MessageService, error: HttpErrorResponse, t: (key: string) => string): void {
  const detail = error.error?.message || t('toast.serverErrorDetail');

  messageService.add({
    severity: 'error',
    summary: t('toast.serverError'),
    detail: detail,
    life: 5000
  });
}

/**
 * Handles network errors (status code 0)
 *
 * Shows a connection error message to the user.
 *
 * @param messageService - PrimeNG message service for notifications
 */
function handleNetworkError(messageService: MessageService, t: (key: string) => string): void {
  messageService.add({
    severity: 'error',
    summary: t('toast.connectionError'),
    detail: t('toast.connectionErrorDetail'),
    life: 5000
  });
}

/**
 * Handles other HTTP errors
 *
 * Shows a generic error message with details from the API if available.
 *
 * @param messageService - PrimeNG message service for notifications
 * @param error - The HTTP error response
 */
function handleGenericError(messageService: MessageService, error: HttpErrorResponse, t: (key: string) => string): void {
  const detail = error.error?.message || t('toast.unexpectedError');

  messageService.add({
    severity: 'error',
    summary: t('toast.error'),
    detail: detail,
    life: 5000
  });
}
